import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { EventEmitter, Injectable } from '@angular/core';
import { AppTextService } from '@fi-shared/app-text';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { cloneDeep } from '@fi-shared/utils/clone-deep';
import { isEqual } from '@fi-shared/utils/is-equal';
import { LoadingService } from '@fi-shared/fi-loading/loading.service';
import { FiStateChangeService } from '@fi-shared/fi-state-proxy/fi-state-change.service';
import { Transition } from '@uirouter/core';
import { Ng2StateDeclaration, RawParams, StateDeclaration } from '@uirouter/angular';
import { IFiDialogOptions, IFiDialogResult } from '@fi-shared/fi-dialogs/fi-dialogs';
import { Subscription } from 'rxjs';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { FiBrowserEventsService } from '@fi-shared/fi-shared-services/fi-browser-events.service';
import { isDefined } from '@fi-shared/utils';
import { FiDialogCloseReason } from '@fi-shared/fi-dialogs/fi-dialogs-close-reason';

type TObjectStateFn = (isReceivingInitialObject?: boolean) => any;

@Injectable()
export class SaveChangesWarningService {
    private watchingState: StateDeclaration;
    private watchingParams: RawParams;
    private initialStateObjects: any[];
    private objectsFn: TObjectStateFn[];

    private started = false;

    private confirmDialog: IFiDialogResult = undefined;

    private isDialogOpened = false;
    private ignoreBeforeUnloadEvent = false;
    private startSub: Subscription;
    private successSub: Subscription;
    private windowBeforeUnloadSub: Subscription;

    constructor(
        private appTextService: AppTextService,
        private fiDialogsService: FiDialogsService,
        private fiStateService: FiStateService,
        private fiStateChangeService: FiStateChangeService,
        private fiLoading: LoadingService,
        private userDataService: FiUserDataService,
        private fiBrowserEventsService: FiBrowserEventsService,
    ) {}

    // https://chainformation.atlassian.net/browse/FI-4227
    withIgnoringBeforeUnloadEvent(callback: () => void) {
        this.ignoreBeforeUnloadEvent = true;
        callback();
        this.ignoreBeforeUnloadEvent = false;
    }

    private compareParams(params1: RawParams, params2: RawParams) {
        if (params1 === params2) return true;

        if (!params1 || !params2) return false;

        const paramNamesToIgnore: string[] = ['#'];
        if (this.watchingState.params) {
            Object.getOwnPropertyNames(this.watchingState.params).forEach((paramName) =>
                paramNamesToIgnore.push(paramName),
            );
        }

        let result = true;
        Object.getOwnPropertyNames(params1).forEach((prop) => {
            if (paramNamesToIgnore.indexOf(prop) === -1 && params2[prop] !== params1[prop]) result = false;
        });

        return result;
    }

    private isTargetStateParent(fromState: StateDeclaration, toState: StateDeclaration): boolean {
        return fromState.name.includes(toState.name);
    }

    private isStateEqualOrChild(parentState: StateDeclaration, childState: StateDeclaration) {
        return (
            childState.name.indexOf(parentState.name) === 0 /* ||
            (parentState.alias && childState.name.indexOf(parentState.alias)=== 0)*/
        );
    }

    private editObjectsEquals() {
        if (!this.initialStateObjects) return false;

        // all object states should be equal
        let equal = true;
        for (let idx = 0; idx < this.initialStateObjects.length && equal; idx++) {
            if (!this.skipObjectCheckIndexes?.includes(idx)) {
                const initialStateObject = this.initialStateObjects[idx];
                const finalStateObject = this.objectsFn[idx]();
                equal = isEqual(initialStateObject, finalStateObject);
            }
        }
        return equal;
    }

    private tempCustomWarningDialogData: IFiDialogOptions<null, null>;
    start(objectFn: TObjectStateFn, customWarningDialogData?: IFiDialogOptions<null, null>) {
        this.startForMultipleObjects([objectFn], customWarningDialogData);
    }

    private _closeDialogOnTransition: boolean;
    private isOnlyDialogWatching: boolean;

    set closeDialogOnTransition(value: boolean) {
        this._closeDialogOnTransition = value;
        if (value) {
            this.handleStartStateChange();
            return;
        }
        if (this.isOnlyDialogWatching) {
            this.stop();
            return;
        }
    }

    startForMultipleObjects(objectsFn: TObjectStateFn[], customWarningDialogData?: IFiDialogOptions<null, null>) {
        this.stop();

        this.started = true;

        this.watchingState = this.fiStateService.current;
        this.watchingParams = this.fiStateService.params;

        this.initialStateObjects = objectsFn ? objectsFn.map((objectFn) => cloneDeep(objectFn(true))) : undefined;
        this.objectsFn = objectsFn;

        this.handleStartStateChange();
        this.successSub = this.fiStateChangeService.onSuccess$.subscribe((transition) => {
            if (
                !(
                    this.isStateEqualOrChild(this.watchingState, transition.to()) &&
                    this.compareParams(this.watchingParams, transition.params())
                )
            ) {
                this.stop();
            }
        });
        this.tempCustomWarningDialogData = customWarningDialogData;
        this.handleWindowBeforeUnloadEvent();
    }

    private handleStartStateChange() {
        if (isDefined(this.startSub)) {
            this.isOnlyDialogWatching = false;
            return;
        }

        this.isOnlyDialogWatching = true;
        this.startSub = this.fiStateChangeService.onStart$.subscribe((transition) => {
            this.onStateChangeStart(
                transition,
                transition.to(),
                transition.params(),
                transition.from(),
                this.fiStateService.getCurrentStateParams(),
            );
        });
    }

    resetInitialObjectByIdx(idx: number) {
        this.initialStateObjects[idx] = cloneDeep(this.objectsFn[idx]());
    }

    public showConfirmDialog() {
        const dialogData = {
            ...this.defaultDialogData,
            ...this.tempCustomWarningDialogData,
        };
        const dialogConfig = {
            ...this.defaultDialogData.config,
            ...this.tempCustomWarningDialogData?.config,
        };
        return this.fiDialogsService.notify({ ...dialogData, config: dialogConfig });
    }

    public hasChanges() {
        return this.started && !this.editObjectsEquals();
    }

    preventParentStateObjCheck: boolean;
    parentStateObjIndexes: number[];

    private skipObjectCheckIndexes: number[] = [];
    private onStateChangeStart(
        transition: Transition,
        toState: Ng2StateDeclaration,
        toParams: RawParams,
        fromState: Ng2StateDeclaration,
        fromParams: RawParams,
    ) {
        if (!this.userDataService.getUserData()) return;

        // pathname + query string
        const prevUrl = window.location.href.substr(window.location.origin.length);

        if (this._closeDialogOnTransition) {
            this.fiLoading.finishLoading();
            window.history.pushState({}, (<any>window).title, prevUrl);
            transition.abort();

            setTimeout(() => {
                this.fiStateChangeService.triggerTransitionCancelled();
                this.fiDialogsService.triggerTransitionCloseDialog$();
            });

            return;
        }

        // skip unwanted object check on history back
        this.skipObjectCheckIndexes = [];
        if (
            this.isTargetStateParent(this.watchingState, toState) &&
            this.preventParentStateObjCheck &&
            this.parentStateObjIndexes?.length
        ) {
            this.skipObjectCheckIndexes = this.parentStateObjIndexes;
        }

        // check is leaving page is a page where service has been started
        if (
            !this.isStateEqualOrChild(this.watchingState, fromState) ||
            !this.compareParams(this.watchingParams, fromParams)
        )
            return;

        if (this.isStateEqualOrChild(this.watchingState, toState) && this.compareParams(this.watchingParams, toParams))
            return;

        this.fiLoading.finishLoading();

        if (this.editObjectsEquals() && !this.isDialogOpened) {
            return;
        }

        window.history.pushState({}, (<any>window).title, prevUrl);

        transition.abort();

        if (this.isDialogOpened) {
            return;
        }

        setTimeout(() => {
            this.confirmDialog = this.showConfirmDialog();

            this.confirmDialog
                .onNoButtonClick(() => {
                    this.onDialogClose();
                    this.fiStateChangeService.triggerTransitionCancelled();
                })
                .onYesButtonClick(() => {
                    this.onDialogClose();

                    this.stop();

                    // check is "back"
                    if (prevUrl === this.fiStateService.href(toState.name, toParams)) {
                        window.history.back();
                        return;
                    }

                    // check is reload
                    if (toState.name === fromState.name)
                        this.fiStateService.transitionTo(fromState.name, toParams, {
                            reload: true,
                            inherit: false,
                            notify: true,
                        });
                    else this.fiStateService.go(toState.name, toParams);

                    this.tempCustomWarningDialogData = undefined;
                });

            this.isDialogOpened = true;
        });
    }

    private onDialogClose() {
        this.isDialogOpened = false;
        this.onSaveChangesCloseConfirmed.emit();
    }

    onSaveChangesCloseConfirmed = new EventEmitter();

    private handleWindowBeforeUnloadEvent() {
        this.windowBeforeUnloadSub = this.fiBrowserEventsService
            .getWindowEventSubject$('beforeunload')
            .subscribe((event) => {
                if (this.ignoreBeforeUnloadEvent) {
                    return;
                }

                if (this.started) {
                    if (this.editObjectsEquals()) {
                        this.stop();
                        return;
                    } else {
                        event.returnValue = false;
                    }

                    if (this.confirmDialog) this.confirmDialog.close();

                    return this.appTextService.get('common.leave_page_message');
                }
            });
    }

    stop() {
        this.started = false;
        this.initialStateObjects = undefined;
        this.objectsFn = undefined;

        if (this.startSub) {
            this.startSub.unsubscribe();
            this.startSub = undefined;
        }
        if (this.successSub) {
            this.successSub.unsubscribe();
            this.successSub = undefined;
        }
        if (this.windowBeforeUnloadSub) {
            this.windowBeforeUnloadSub.unsubscribe();
            this.windowBeforeUnloadSub = undefined;
        }
    }

    readonly defaultDialogData: Readonly<IFiDialogOptions<null, null>> = {
        headerIconClass: 'far fa-trash red',
        colorClass: 'red',
        isBigSizeTopBorder: true,
        preventUppercaseButtonLabel: true,
        headerText: this.appTextService.get('common.discard_changes_header'),
        messageText: this.appTextService.get('common.leave_page_without_saving_warning_message'),
        yesButtonText: this.appTextService.get('common.discard_changes'),
        noButtonText: this.appTextService.get('common.cancel'),
        isNoButtonPrimaryColor: true,
        config: {
            panelClass: ['fi-rounded-dialog-panel', 'fi-dialog-no-button-primary-red'],
            autoFocus: false,
        },
    };
}
